import { Component, ElementRef, HostListener, inject, Input, ViewChild, type OnInit } from '@angular/core';
import { NewplaBaseComponent2, NewplaPageUtils } from '@newpla/ng-common';
import { Bs5ModalCtrl } from '@newpla/ng-lib';
import { NpTaskAll } from '@newpla/ts-common';
import { NgbCarousel, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NpxCommonModule } from '../../../../newpla-npx-ng17/src/_shared-modules/npx-common.module';
import { NpxFormsModule } from '../../../../newpla-npx-ng17/src/_shared-modules/npx-forms.module';
import { SlsNovaPageLoading } from '../../config/sls-nova-page-loading';

@Component({
  selector: 'app-image-carousel-modal.component',
  standalone: true,
  imports: [NpxCommonModule, NpxFormsModule],
  templateUrl: './image-carousel-modal.component.html',
  styleUrl: './image-carousel-modal.component.scss',
})
export class ImageCarouselModalComponent extends NewplaBaseComponent2 {

  ///////////////////////////////////////////////////////////////////////////////////////////////////// BROWSER INIT
  browserInited: boolean = false;
  override browserNgOnInit = async () => {

    this.modalCtrl.toggleFullscreen();
    this.activeIndex = this.initialIndex >= 0 && this.initialIndex < this.images.length ? this.initialIndex : 0;

    try {
      const taskAll = new NpTaskAll();
      taskAll.add(SlsNovaPageLoading.wait());
      await taskAll.run();

      this.browserInited = true;
      this.scrollActiveThumbnail();
    } catch (e) {
      NewplaPageUtils.browserInitError(e);
      this.activeModal.dismiss();
    }
  };

  ///////////////////////////////////////////////////////////////////////////////////////////////////// DATA 

  @Input() images: any[] = [];
  @Input() initialIndex = 0;

  @ViewChild('carousel') carousel?: NgbCarousel;
  @ViewChild('thumbnailList') thumbnailList?: ElementRef<HTMLElement>;

  dataMap: any = {
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////// INJECT 

  activeModal = inject(NgbActiveModal);
  modalCtrl = new Bs5ModalCtrl({ drag: false, fullscreen: true });
  private elementRef = inject(ElementRef<HTMLElement>);

  /////////////////////////////////////////////////////////////////////////////////////////////////////

  activeIndex = 0;
  zoom = 1;
  rotation = 0;
  translate = { x: 0, y: 0 };
  loadedImageIndexes = new Set<number>();
  failedImageIndexes = new Set<number>();

  /* dragStart : 드래그 시작 상태 저장
  드래그 시작 시점 기준으로 자연스럽게 이미지 이동
  */
  private dragStart?: {
    pointerId: number;
    x: number;
    y: number;
    translateX: number;
    translateY: number;
  };

  // 확대축소
  zoomIn() {
    this.zoom = Math.min(this.zoom + 0.25, 5);
    this.clampTranslate();
  }

  zoomOut() {
    this.zoom = Math.max(this.zoom - 0.25, 1);

    if (this.zoom === 1) {
      this.resetZoom();
    } else {
      this.clampTranslate();
    }
  }

  resetZoom() {
    this.zoom = 1;
    this.translate = { x: 0, y: 0 };
  }

  // 더블클릭: 원래 크기면 2배, 확대돼 있으면 원래 크기
  toggleZoom() {
    if (this.zoom === 1) {
      this.zoom = 2;
    } else {
      this.resetZoom();
    }
  }

  // 마우스 휠: 위로 굴리면 확대, 아래로 굴리면 축소
  onWheel(event: WheelEvent) {
    event.preventDefault();

    if (event.deltaY < 0) {
      this.zoomIn();
    } else if (event.deltaY > 0) {
      this.zoomOut();
    }
  }

  rotateLeft() {
    this.rotation = (this.rotation + 270) % 360;
    this.translate = { x: 0, y: 0 };
  }

  rotateRight() {
    this.rotation = (this.rotation + 90) % 360;
    this.translate = { x: 0, y: 0 };
  }

  // 확대된 이미지 드래그
  onPointerDown(event: PointerEvent) {
    if (this.zoom <= 1) {
      return;
    }

    // 브라우저 기본 동작 방지
    event.preventDefault();

    const target = event.currentTarget as HTMLElement;
    target.setPointerCapture(event.pointerId);

    this.dragStart = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      translateX: this.translate.x,
      translateY: this.translate.y
    };
  }

  onPointerMove(event: PointerEvent) {
    if (!this.dragStart || this.dragStart.pointerId !== event.pointerId) {
      return;
    }

    event.preventDefault();

    this.translate = this.getClampedTranslate({
      x: this.dragStart.translateX + event.clientX - this.dragStart.x,
      y: this.dragStart.translateY + event.clientY - this.dragStart.y
    });
  }

  onPointerUp(event: PointerEvent) {
    if (this.dragStart?.pointerId !== event.pointerId) {
      return;
    }

    const target = event.currentTarget as HTMLElement;
    target.releasePointerCapture(event.pointerId);

    this.dragStart = undefined;
  }

  getImageTransform() {
    return `translate(${this.translate.x}px, ${this.translate.y}px) rotate(${this.rotation}deg) scale(${this.zoom})`;
  }

  getImageUrl(image: any): string {
    return typeof image === 'string' ? image : image?.url;
  }

  getImageFilename(image: any): string {
    return typeof image === 'string' ? '' : image?.filename ?? '';
  }

  // V-INFO 사진은 분류 제목(vinfoImgTitle, 예: 입고검수_2026-10-21 17:44)이 있다
  getImageTitle(image: any): string {
    return typeof image === 'string' ? '' : image?.vinfoImgTitle ?? '';
  }

  imageLoadComplete(index: number) {
    this.loadedImageIndexes.add(index);
    this.failedImageIndexes.delete(index);
  }

  imageLoadFailed(index: number) {
    this.loadedImageIndexes.add(index);
    this.failedImageIndexes.add(index);
  }

  isImageLoaded(index: number): boolean {
    return this.loadedImageIndexes.has(index);
  }

  isImageLoadFailed(index: number): boolean {
    return this.failedImageIndexes.has(index);
  }

  @HostListener('document:keydown.arrowleft')
  showPreviousImage() {
    this.carousel?.prev();
  }

  @HostListener('document:keydown.arrowright')
  showNextImage() {
    this.carousel?.next();
  }

  private clampTranslate(): void {
    this.translate = this.getClampedTranslate(this.translate);
  }

  private getClampedTranslate(translate: { x: number, y: number }): { x: number, y: number } {
    const image = this.elementRef.nativeElement.querySelector('.carousel-item.active .zoom-image') as HTMLImageElement | null;
    const viewer = image?.closest('.image-viewer') as HTMLElement | null;
    if (!image || !viewer) {
      return translate;
    }

    const rotated = this.rotation % 180 !== 0;
    const imageWidth = (rotated ? image.offsetHeight : image.offsetWidth) * this.zoom;
    const imageHeight = (rotated ? image.offsetWidth : image.offsetHeight) * this.zoom;
    const maxX = Math.max(0, (imageWidth - viewer.clientWidth) / 2);
    const maxY = Math.max(0, (imageHeight - viewer.clientHeight) / 2);

    return {
      x: Math.max(-maxX, Math.min(maxX, translate.x)),
      y: Math.max(-maxY, Math.min(maxY, translate.y))
    };
  }


  // 썸네일 이미지 선택
  selectImage(index: number) {
    this.activeIndex = index;
    this.resetImageTransform();
    this.carousel?.select(`slide-${index}`);
  }

  // 슬라이드 이동
  onSlide(slideEvent: any) {
    const current = slideEvent.current;

    if (typeof current === 'string' && current.startsWith('slide-')) {
      this.activeIndex = Number(current.replace('slide-', ''));
      this.resetImageTransform();
      this.scrollActiveThumbnail();
    }
  }

  // 선택한 썸네일이 썸네일 줄 밖에 있으면 보이게 스크롤
  private scrollActiveThumbnail(): void {
    setTimeout(() => {
      const thumbnail = this.thumbnailList?.nativeElement.querySelector('.thumbnail-btn.active') as HTMLElement | null;
      thumbnail?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    });
  }

  private resetImageTransform(): void {
    this.resetZoom();
    this.rotation = 0;
  }

}
